import BtsSection from "./components/BtsSection";
import RecommendationsSection from "./components/RecommendationsSection";
import SeriesRail from "./components/SeriesRail";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * The series page while it loads, section for section and with the page's
 * own breakpoints (SeriesDetailsModule and its components), so nothing moves
 * when the show lands. The Cast, the BTS and the Recommendations are drawn
 * too, although not every series has them today: the data is being filled
 * in, and a rail that vanishes moves the page as much as one that appears.
 */
const SeriesDetailsSkeleton = () => (
  <div
    data-test-id="series-details-skeleton"
    className="mx-auto flex max-w-md flex-col gap-8 px-4 pb-28 sm:max-w-5xl sm:pb-12"
  >
    <div className="border-neutral-60 hidden border-b pb-4 sm:block">
      <Skeleton className="h-5.25 w-64" />
    </div>

    {/* Hero: full-bleed artwork on mobile, the poster and its column from
          `sm`. */}
    <section className="flex flex-col gap-4 sm:flex-row sm:gap-6">
      <Skeleton className="-mx-4 aspect-3/4 rounded-none sm:mx-0 sm:w-56 sm:shrink-0 sm:rounded-lg" />
      <div className="flex flex-1 flex-col gap-4">
        <Skeleton className="hidden h-8.5 w-2/3 sm:block" />
        <Skeleton className="hidden h-6 w-40 sm:block" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <Skeleton className="hidden h-8.75 w-20 sm:block" />
        <div className="sm:border-neutral-60 flex items-center gap-3 sm:border-t sm:pt-6">
          {/* Play: a sticky bottom bar on mobile, inline from `sm` — the
                same bar as SeriesActions', Zendesk lift included. */}
          <div
            data-bottom-bar
            className="from-background fixed inset-x-0 bottom-0 z-60 flex justify-center bg-gradient-to-t p-4 sm:static sm:z-auto sm:bg-none sm:p-0"
          >
            <Skeleton className="h-12 w-full max-w-md rounded-full sm:w-40" />
          </div>
          <Skeleton className="size-10 rounded-full" />
          <Skeleton className="size-10 rounded-full" />
        </div>
      </div>
    </section>

    {/* Episodes: a strip below `md`, then two rows of five, of seven from
          `lg`. */}
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5.5 w-32" />
        <Skeleton className="h-4 w-16" />
      </div>
      <div className="-mx-4 flex gap-2 overflow-hidden px-4 *:w-24 *:shrink-0 md:mx-0 md:grid md:grid-cols-5 md:gap-3 md:px-0 md:*:w-auto lg:grid-cols-7 md:[&>*:nth-child(n+11)]:hidden lg:[&>*:nth-child(n+11)]:flex lg:[&>*:nth-child(n+15)]:hidden">
        {Array.from({ length: 14 }, (_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <Skeleton className="aspect-3/4" />
            <Skeleton className="h-3.25 w-1/2" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2 md:hidden">
        <Skeleton className="h-1 rounded-full" />
        <div className="flex justify-between">
          <Skeleton className="h-3.25 w-20" />
          <Skeleton className="h-3.25 w-24" />
        </div>
      </div>
    </section>

    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5.5 w-16" />
        <Skeleton className="h-4 w-16" />
      </div>
      <SeriesRail static itemClassName="gap-3 *:w-25 md:gap-6 md:*:w-36">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <Skeleton className="aspect-square w-full rounded-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </SeriesRail>
    </section>

    {/* The sections' own skeletons: each keeps drawing it while its query
          runs, so the rails don't vanish and come back once the show lands. */}
    <BtsSection.Skeleton />
    <RecommendationsSection.Skeleton />
  </div>
);

export default SeriesDetailsSkeleton;
